/**
 * TextField component
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 */

// Library
import { BaseSyntheticEvent, ChangeEvent, ReactNode, useEffect, useState } from 'react';

// CSS Style for this module
import styles from './textField.module.scss';

type HTMLInputMode =
	| 'text'
	| 'search'
	| 'email'
	| 'tel'
	| 'url'
	| 'none'
	| 'numeric'
	| 'decimal'
	| undefined;

/**
 * Interface for TextField props
 */
interface TextFieldProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * preset type for the html input tag
	 */
	type?: string;
	/**
	 * inputMode for the html input tag
	 */
	inputMode?: HTMLInputMode;
	/**
	 * label of the input element
	 */
	label?: string;
	/**
	 * Help text to provide additional guidance to the user
	 * Can be a string or a ReactNode for complex formatting
	 */
	helpText?: string | ReactNode;
	/**
	 * Default text that is displayed within the field
	 */
	placeholder?: string;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * Boolean for determining whether this is a required field
	 */
	status?: 'valid' | 'error';
	/**
	 * Number for determining the text limit input
	 */
	maxLength?: number;
	/**
	 * onChange function for keeping track field value
	 * debounce is built in
	 */
	handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	/**
	 * triggered if the user presses enter key for submitting a value.
	 * debounce is built in
	 */
	handleSubmit?: () => void;
	/**
	 * default value for edit mode
	 */
	defaultValue?: string;
	/**
	 * Pattern as regular expression of valid characters in the field value
	 */
	pattern?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/** Element identifier selector for test */
	testId?: string;
}

/**
 * Text fields let users enter and edit text.
 * @param {TextFieldProps} Prop
 * @returns  an styled input element
 */
export const TextField = ({
	customStyles,
	type = 'text',
	inputMode,
	label = '',
	helpText,
	placeholder = 'Please input a new value',
	errorMsg = 'Please fill up the field',
	status = 'valid',
	maxLength,
	testId,
	handleChange = () => {
		return;
	},
	handleSubmit = () => {
		return;
	},
	defaultValue = '',
	pattern,
	disabled = false,
}: TextFieldProps) => {
	const TEXTAREA = 'textarea';
	const [value, setValue] = useState(defaultValue);

	useEffect(() => {
		setValue(defaultValue);
	}, [defaultValue]);

	const evaluateValue = (event: BaseSyntheticEvent) => {
		const isValid = pattern ? RegExp(pattern).test(event.target.value) : true;
		if (isValid) {
			setValue(event.target.value);
		}
		return isValid;
	};

	const onType = (event: BaseSyntheticEvent) => {
		if (evaluateValue(event)) {
			handleChange(event as ChangeEvent<HTMLInputElement>);
		}
	};

	const handleKeyDown = (e: { keyCode: number }) => {
		// only triggered by the enter key
		if (e.keyCode === 13) {
			handleSubmit();
		}
	};

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			{label && (
				<label htmlFor={label} className={styles.label}>
					<p>{label}</p>
				</label>
			)}
			{helpText && (
				<div className={styles.helpText}>
					{typeof helpText === 'string' ? <p>{helpText}</p> : helpText}
				</div>
			)}
			{type === TEXTAREA ? (
				<textarea
					className={[
						styles.textArea,
						styles[`textArea-${status}`],
						styles[`textArea-disabled-${disabled}`],
					].join(' ')}
					placeholder={placeholder}
					onChange={onType}
					disabled={disabled}
					data-testid={testId}
				/>
			) : (
				<input
					name={label}
					className={[
						styles.textField,
						styles[`textField-${status}`],
						styles[`textField-disabled-${disabled}`],
					].join(' ')}
					type={type}
					inputMode={inputMode}
					placeholder={placeholder}
					value={value}
					onChange={onType}
					onKeyDown={handleKeyDown}
					maxLength={maxLength ? maxLength : undefined}
					disabled={disabled}
					pattern={pattern}
					data-testid={testId}
				/>
			)}
			<p
				className={[styles['error-msg'], status !== 'error' && styles['error-msg-hide']].join(' ')}
			>
				{errorMsg}
			</p>
		</div>
	);
};
